import React, { useMemo, useState } from 'react';
import { Alert, Card, Descriptions, Select, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import quotationService, { type Quotation } from '@/services/quotation.service';
import { formatMoney, formatPercent, formatNumber, UAE_MONEY_PROFILE } from '@/utils/format';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';

const { Text } = Typography;

export type QuotationRevisionCompareResult = {
  previous: { id: string; quotation_number: string; revision_number: number };
  current: { id: string; quotation_number: string; revision_number: number };
  classification: string;
  reapproval_materiality: string;
  categories: string[];
  field_deltas: Array<{ field: string; category: string; previous: unknown; current: unknown }>;
  line_deltas: Array<{ key: string; change: string }>;
  commercial_impact: {
    currency: string;
    previous_selling_price: number;
    revised_selling_price: number;
    selling_delta: number;
    previous_cost: number | null;
    revised_cost: number | null;
    cost_delta: number | null;
    previous_margin: number | null;
    revised_margin: number | null;
    margin_delta: number | null;
    previous_margin_pct: number | null;
    revised_margin_pct: number | null;
    margin_pct_delta_pp: number | null;
    previous_header_discount_pct: number;
    revised_header_discount_pct: number;
    previous_header_discount_amount: number;
    revised_header_discount_amount: number;
    previous_line_discount_total: number;
    revised_line_discount_total: number;
  };
};

function money(amount: number | null | undefined, currency: string) {
  if (amount === null || amount === undefined) return '—';
  return formatMoney(amount, { ...UAE_MONEY_PROFILE, currency });
}

function deltaTag(delta: number | null | undefined, currency: string, suffix = '') {
  if (delta === null || delta === undefined) return <Tag>No change / n/a</Tag>;
  if (Math.abs(delta) < 0.005) return <Tag color="default">No change</Tag>;
  const color = delta < 0 ? 'error' : 'success';
  const arrow = delta < 0 ? '▼' : '▲';
  return (
    <Tag color={color}>
      {arrow} {money(Math.abs(delta), currency)}
      {suffix}
    </Tag>
  );
}

type Props = {
  quotation: Quotation;
  /** When true, omit outer Card (Wave 8 workspace tab). */
  embedded?: boolean;
};

export const QuotationRevisionComparePanel: React.FC<Props> = ({ quotation, embedded }) => {
  const familyQuery = useQuery({
    queryKey: ['quotation-revision-family', quotation.id],
    queryFn: () => quotationService.listRevisionFamily(quotation.id),
  });

  const family = familyQuery.data || [];
  const defaultAgainst =
    quotation.parent_quotation_id ||
    family.find((q) => q.revision_number === Number(quotation.revision_number) - 1)?.id ||
    family.find((q) => q.id !== quotation.id)?.id;

  const [againstId, setAgainstId] = useState<string | undefined>(undefined);
  const withId = againstId || defaultAgainst;

  const compareQuery = useQuery({
    queryKey: ['quotation-revision-compare', quotation.id, withId],
    enabled: !!withId && withId !== quotation.id,
    queryFn: () => quotationService.compareRevisions(quotation.id, withId!),
  });

  const comparison = compareQuery.data as QuotationRevisionCompareResult | undefined;
  const impact = comparison?.commercial_impact;
  const currency = impact?.currency || quotation.currency || 'AED';

  const againstOptions = useMemo(
    () =>
      family
        .filter((q) => q.id !== quotation.id)
        .map((q) => ({
          value: q.id,
          label: `${q.quotation_number} · ${displayQuotationRevisionLabel(q.revision_number)} — ${q.status}`,
        })),
    [family, quotation.id]
  );

  if (family.length < 2 && !quotation.parent_quotation_id) {
    return embedded ? (
      <Alert type="info" showIcon message="No prior revision in this family to compare." />
    ) : null;
  }

  const body = (
    <>
      {!embedded ? null : (
        <Select
          style={{ minWidth: 280, marginBottom: 12 }}
          placeholder="Compare against…"
          value={withId}
          onChange={setAgainstId}
          options={againstOptions}
          loading={familyQuery.isFetching}
        />
      )}
      {!withId ? (
        <Alert type="info" showIcon message="Select another revision in this family to compare." />
      ) : compareQuery.isFetching ? (
        <Text type="secondary">Loading comparison…</Text>
      ) : compareQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={(compareQuery.error as Error)?.message || 'Comparison failed'}
        />
      ) : comparison && impact ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Tag color="blue">
              {displayQuotationRevisionLabel(comparison.previous.revision_number)} →{' '}
              {displayQuotationRevisionLabel(comparison.current.revision_number)}
            </Tag>
            <Tag color="purple">{comparison.classification}</Tag>
            <Tag color="orange">Reapproval: {comparison.reapproval_materiality}</Tag>
            {comparison.categories.map((c) => (
              <Tag key={c}>{c}</Tag>
            ))}
          </Space>

          <Descriptions size="small" bordered column={1} title="Commercial impact">
            <Descriptions.Item label="Selling price">
              {money(impact.previous_selling_price, currency)} → {money(impact.revised_selling_price, currency)}{' '}
              {deltaTag(impact.selling_delta, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Estimated cost">
              {money(impact.previous_cost, currency)} → {money(impact.revised_cost, currency)}{' '}
              {deltaTag(impact.cost_delta, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Margin">
              {money(impact.previous_margin, currency)} → {money(impact.revised_margin, currency)}{' '}
              {deltaTag(impact.margin_delta, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Margin %">
              {impact.previous_margin_pct == null
                ? '—'
                : formatPercent(impact.previous_margin_pct, {
                    semantics: 'whole',
                    maximumFractionDigits: 2,
                  })}{' '}
              →{' '}
              {impact.revised_margin_pct == null
                ? '—'
                : formatPercent(impact.revised_margin_pct, {
                    semantics: 'whole',
                    maximumFractionDigits: 2,
                  })}{' '}
              {impact.margin_pct_delta_pp == null ? (
                <Tag>n/a</Tag>
              ) : Math.abs(impact.margin_pct_delta_pp) < 0.005 ? (
                <Tag>No change</Tag>
              ) : (
                <Tag color={impact.margin_pct_delta_pp < 0 ? 'error' : 'success'}>
                  {impact.margin_pct_delta_pp < 0 ? '▼' : '▲'}{' '}
                  {formatNumber(Math.abs(impact.margin_pct_delta_pp), { maximumFractionDigits: 2 })} pp
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Header discount">
              {formatPercent(impact.previous_header_discount_pct, {
                semantics: 'whole',
                maximumFractionDigits: 2,
              })}{' '}
              / {money(impact.previous_header_discount_amount, currency)} →{' '}
              {formatPercent(impact.revised_header_discount_pct, {
                semantics: 'whole',
                maximumFractionDigits: 2,
              })}{' '}
              / {money(impact.revised_header_discount_amount, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Line discounts (sum)">
              {money(impact.previous_line_discount_total, currency)} →{' '}
              {money(impact.revised_line_discount_total, currency)}
            </Descriptions.Item>
          </Descriptions>

          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.key}
            dataSource={comparison.line_deltas}
            locale={{ emptyText: 'No line changes' }}
            columns={[
              { title: 'Line', dataIndex: 'key', key: 'key', width: 120 },
              { title: 'Change', dataIndex: 'change', key: 'change' },
            ]}
          />

          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.field}
            dataSource={comparison.field_deltas.filter((f) =>
              ['TERMS', 'TEXT', 'COMMERCIAL', 'HEADER'].includes(String(f.category).toUpperCase())
            ).slice(0, 40)}
            locale={{ emptyText: 'No terms / text differences' }}
            title={() => 'Terms & text changes'}
            columns={[
              { title: 'Field', dataIndex: 'field', key: 'field', ellipsis: true },
              { title: 'Category', dataIndex: 'category', key: 'category', width: 120 },
              {
                title: 'Previous',
                dataIndex: 'previous',
                key: 'previous',
                render: (v) => (
                  <div style={{ whiteSpace: 'pre-wrap', maxWidth: 280 }}>{String(v ?? '')}</div>
                ),
              },
              {
                title: 'Current',
                dataIndex: 'current',
                key: 'current',
                render: (v) => (
                  <div style={{ whiteSpace: 'pre-wrap', maxWidth: 280 }}>{String(v ?? '')}</div>
                ),
              },
            ]}
          />

          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.field}
            dataSource={comparison.field_deltas.slice(0, 40)}
            locale={{ emptyText: 'No field differences' }}
            columns={[
              { title: 'Field', dataIndex: 'field', key: 'field', ellipsis: true },
              { title: 'Category', dataIndex: 'category', key: 'category', width: 160 },
              {
                title: 'Previous',
                dataIndex: 'previous',
                key: 'previous',
                ellipsis: true,
                render: (v) => String(v ?? ''),
              },
              {
                title: 'Current',
                dataIndex: 'current',
                key: 'current',
                ellipsis: true,
                render: (v) => String(v ?? ''),
              },
            ]}
          />
          {comparison.field_deltas.length > 40 ? (
            <Text type="secondary">Showing first 40 of {comparison.field_deltas.length} field deltas.</Text>
          ) : null}
        </Space>
      ) : null}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Card
      size="small"
      title="Revision comparison & commercial impact"
      style={{ marginBottom: 16 }}
      extra={
        <Select
          style={{ minWidth: 280 }}
          placeholder="Compare against…"
          value={withId}
          onChange={setAgainstId}
          options={againstOptions}
          loading={familyQuery.isFetching}
        />
      }
    >
      {body}
    </Card>
  );
};

export default QuotationRevisionComparePanel;
